import { Component, ElementRef, EventEmitter, Input, OnInit, OnChanges, SimpleChanges, Output, ViewChild, OnDestroy, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as QRCode from 'qrcode';
import { TranslatePipe, TranslateService } from '@dental-ui/shared/i18n';

@Component({
  selector: 'lib-mobile-upload-qr-dialog',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  templateUrl: './mobile-upload-qr-dialog.component.html',
  styles: [`
    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1100;
    }
    .qr-dialog-modal {
      background: var(--bg-card, #fff);
      border-radius: 8px;
      width: 100%;
      max-width: 440px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
      color: var(--text-primary, #333);
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }
    .modal-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 16px 20px;
      border-bottom: 1px solid var(--border-color, #eee);
    }
    .modal-header h2 {
      margin: 0;
      font-size: 1.25rem;
      font-weight: 600;
    }
    .close-btn {
      background: none;
      border: none;
      cursor: pointer;
      color: var(--text-secondary, #666);
      padding: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .close-btn:hover {
      color: var(--text-primary, #333);
    }
    .modal-body {
      padding: 24px 20px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
    }
    .instruction-text {
      font-size: 0.95rem;
      color: var(--text-secondary, #666);
      margin-bottom: 20px;
      line-height: 1.5;
    }
    .qr-container {
      background: #fff;
      padding: 12px;
      border-radius: 8px;
      border: 1px solid var(--border-color, #eee);
      margin-bottom: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .qr-container canvas {
      display: block;
      width: 320px !important;
      height: 320px !important;
    }
    .expiry-text {
      font-size: 0.9rem;
      color: var(--text-secondary, #777);
      margin: 0;
    }
    .expired-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 20px 0;
    }
    .error-icon {
      font-size: 48px;
      color: #e53935;
      margin-bottom: 12px;
    }
    .expired-text {
      font-size: 1.1rem;
      font-weight: 500;
      color: var(--text-primary, #333);
      margin-bottom: 20px;
    }
    .modal-footer {
      padding: 12px 20px;
      border-top: 1px solid var(--border-color, #eee);
      display: flex;
      justify-content: flex-end;
    }
    .btn {
      padding: 8px 16px;
      border-radius: 4px;
      font-size: 0.9rem;
      font-weight: 500;
      cursor: pointer;
      border: none;
      transition: all 0.2s ease;
    }
    .btn-primary {
      background: var(--primary-color, #0288d1);
      color: #fff;
    }
    .btn-primary:hover {
      background: var(--primary-color-dark, #01579b);
    }
    .btn-secondary {
      background: var(--bg-button-secondary, #eee);
      color: var(--text-secondary, #333);
    }
    .btn-secondary:hover {
      background: var(--bg-button-secondary-hover, #ddd);
    }
  `]
})
export class MobileUploadQrDialogComponent implements OnInit, OnDestroy, OnChanges {
  private readonly translateService = inject(TranslateService);

  @Input() uploadUrl = '';
  @Input() expiresAt?: string;
  @Output() close = new EventEmitter<void>();
  @Output() generateNew = new EventEmitter<void>();

  @ViewChild('qrCanvas') qrCanvas!: ElementRef<HTMLCanvasElement>;

  timeRemaining = signal<string>('10:00');
  isExpired = signal<boolean>(false);

  private timerInterval: ReturnType<typeof setInterval> | null = null;
  private secondsLeft = 600; // 10 minutes

  ngOnInit(): void {
    this.translateService.loadLibrary('attachment').catch((err: unknown) => console.warn(err));
    this.startCountdown();
    // Render QR code in next microtask to let canvas render first
    setTimeout(() => this.renderQr(), 0);
  }

  ngOnDestroy(): void {
    this.clearTimer();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['uploadUrl'] && !changes['uploadUrl'].isFirstChange()) || (changes['expiresAt'] && !changes['expiresAt'].isFirstChange())) {
      this.startCountdown();
      setTimeout(() => this.renderQr(), 0);
    }
  }

  startCountdown(): void {
    this.clearTimer();
    
    if (this.expiresAt) {
      const expiryTime = new Date(this.expiresAt).getTime();
      const now = new Date().getTime();
      this.secondsLeft = Math.floor((expiryTime - now) / 1000);
      if (this.secondsLeft <= 0) {
        this.isExpired.set(true);
        this.updateTimeDisplay();
        return;
      }
    } else {
      this.secondsLeft = 600;
    }
    
    this.isExpired.set(false);
    this.updateTimeDisplay();

    this.timerInterval = setInterval(() => {
      this.secondsLeft--;
      if (this.secondsLeft <= 0) {
        this.isExpired.set(true);
        this.clearTimer();
      } else {
        this.updateTimeDisplay();
      }
    }, 1000);
  }

  private updateTimeDisplay(): void {
    const minutes = Math.floor(this.secondsLeft / 60);
    const seconds = this.secondsLeft % 60;
    this.timeRemaining.set(`${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`);
  }

  private clearTimer(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  renderQr(): void {
    if (this.qrCanvas && this.uploadUrl) {
      QRCode.toCanvas(this.qrCanvas.nativeElement, this.uploadUrl, {
        width: 320,
        margin: 1,
        color: {
          dark: '#000000',
          light: '#ffffff'
        }
      }, (error) => {
        if (error) {
          console.error('Error generating QR code:', error);
        }
      });
    }
  }

  onGenerateNewQr(): void {
    this.generateNew.emit();
  }

  onClose(): void {
    this.close.emit();
  }
}
